import React, { useEffect, useState, useCallback } from "react";
import API from "../services/api";
import { toast } from "react-toastify";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from "recharts";
import {
  FileBarChart2, Download, RefreshCw, MapPin,
  GraduationCap, TrendingUp, Users, Wallet,
} from "lucide-react";

const formatKES = (amt) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency", currency: "KES", maximumFractionDigits: 0,
  }).format(amt || 0);

const LEVEL_COLORS  = { SECONDARY: "#3B82F6", COLLEGE: "#10B981", UNIVERSITY: "#8B5CF6" };
const STATUS_COLORS = { SUBMITTED: "#F59E0B", APPROVED: "#10B981", REJECTED: "#EF4444", DRAFT: "#9CA3AF" };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-3 py-2 text-xs">
      {label && <p className="font-semibold text-gray-700 mb-1">{label}</p>}
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.fill || p.color }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ title, value, sub, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon size={22} />
    </div>
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{title}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const ReportsPage = () => {
  const [applications, setApplications] = useState([]);
  const [cycles,       setCycles]       = useState([]);
  const [disbursements,setDisbursements]= useState([]);
  const [selectedCycle,setSelectedCycle]= useState("");
  const [loading,      setLoading]      = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [appRes, cycleRes, disbRes] = await Promise.allSettled([
        API.get("/admin/applications", { params: { limit: 500 } }),
        API.get("/cycles"),
        API.get("/disbursements"),
      ]);
      if (appRes.status   === "fulfilled") setApplications(appRes.value.data.data  || []);
      if (cycleRes.status === "fulfilled") setCycles(cycleRes.value.data            || []);
      if (disbRes.status  === "fulfilled") setDisbursements(disbRes.value.data      || []);
    } catch {
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filter by cycle ───────────────────────────────────────────────────────
  const cycle = cycles.find((c) => c._id === selectedCycle);
  const filteredApps = selectedCycle && cycle
    ? applications.filter((a) => a.academicYear === cycle.academicYear)
    : applications;
  const filteredDisb = selectedCycle
    ? disbursements.filter((d) => d.cycle?._id === selectedCycle || d.cycle === selectedCycle)
    : disbursements;

  // ── Summary stats ─────────────────────────────────────────────────────────
  const totalApps     = filteredApps.length;
  const approved      = filteredApps.filter((a) => a.status === "APPROVED").length;
  const rejected      = filteredApps.filter((a) => a.status === "REJECTED").length;
  const submitted     = filteredApps.filter((a) => a.status === "SUBMITTED").length;
  const totalDisbursed= filteredDisb.reduce((s, d) => s + (d.amountAwarded || 0), 0);
  const paidCount     = filteredDisb.filter((d) => d.status === "PAID").length;
  const approvalRate  = totalApps > 0 ? Math.round((approved / totalApps) * 100) : 0;

  // ── By ward ───────────────────────────────────────────────────────────────
  const byWard = {};
  filteredApps.forEach((a) => {
    const ward = a.studentId?.ward?.name || "Unknown";
    if (!byWard[ward]) byWard[ward] = { name: ward, total: 0, approved: 0, rejected: 0 };
    byWard[ward].total++;
    if (a.status === "APPROVED") byWard[ward].approved++;
    if (a.status === "REJECTED") byWard[ward].rejected++;
  });
  const wardData = Object.values(byWard).sort((a, b) => b.total - a.total);

  // ── By school level ───────────────────────────────────────────────────────
  const byLevel = {};
  filteredApps.forEach((a) => {
    const level = a.studentId?.schoolLevel || a.studentId?.ward?.schoolLevel || "UNKNOWN";
    if (!byLevel[level]) byLevel[level] = { name: level, total: 0, approved: 0 };
    byLevel[level].total++;
    if (a.status === "APPROVED") byLevel[level].approved++;
  });
  const levelData = Object.values(byLevel);

  // ── Pie data ──────────────────────────────────────────────────────────────
  const statusPie = [
    { name: "Submitted", value: submitted },
    { name: "Approved",  value: approved  },
    { name: "Rejected",  value: rejected  },
    { name: "Draft",     value: totalApps - submitted - approved - rejected },
  ].filter((d) => d.value > 0);

  // ── CSV export ────────────────────────────────────────────────────────────
  const exportSummaryCSV = () => {
    const header = "Ward,Total Applications,Approved,Rejected,Approval Rate";
    const rows   = wardData.map((w) =>
      `"${w.name}",${w.total},${w.approved},${w.rejected},${w.total > 0 ? Math.round((w.approved / w.total) * 100) : 0}%`
    );
    const csv  = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href  = window.URL.createObjectURL(blob);
    link.download = `bursary_report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const exportFullCSV = () => {
    const header = "Student,Ward,School Level,Institution,Course,Total Fees,Fee Balance,Status,Submitted";
    const rows   = filteredApps.map((a) => [
      `"${a.studentId?.fullName || ""}"`,
      `"${a.studentId?.ward?.name || ""}"`,
      `"${a.studentId?.schoolLevel || ""}"`,
      `"${a.institution?.institutionName || ""}"`,
      `"${a.institution?.courseOrClass || ""}"`,
      a.fees?.totalFees   || 0,
      a.fees?.feeBalance  || 0,
      a.status,
      a.submittedAt ? new Date(a.submittedAt).toLocaleDateString("en-KE") : "",
    ].join(","));
    const csv  = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href  = window.URL.createObjectURL(blob);
    link.download = `applications_full_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-primary">Reports & Analytics</h2>
          <p className="text-gray-500 text-sm mt-1">Bursary programme performance overview</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={fetchData} className="bg-secondary text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-blue-700 active:scale-95 transition">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={exportSummaryCSV} className="bg-gray-700 text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-gray-800 active:scale-95 transition">
            <Download size={14} /> Ward Summary CSV
          </button>
          <button onClick={exportFullCSV} className="bg-green-700 text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-green-800 active:scale-95 transition">
            <Download size={14} /> Full Report CSV
          </button>
        </div>
      </div>

      {/* Cycle filter */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 flex-wrap">
        <FileBarChart2 size={16} className="text-gray-400" />
        <span className="text-sm text-gray-500">Filter by cycle:</span>
        <select
          value={selectedCycle}
          onChange={(e) => setSelectedCycle(e.target.value)}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary transition"
        >
          <option value="">All Cycles (Combined)</option>
          {cycles.map((c) => (
            <option key={c._id} value={c._id}>{c.name} — {c.academicYear}</option>
          ))}
        </select>
        {selectedCycle && cycle && (
          <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
            Deadline: {new Date(cycle.applicationDeadline).toLocaleDateString("en-KE")} · Status: {cycle.status}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-20 gap-3">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Generating report...</p>
        </div>
      ) : (
        <>
          {/* Summary stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Total Applications" value={totalApps}           sub={`${submitted} pending review`}    icon={FileBarChart2} color="bg-blue-50 text-primary" />
            <StatCard title="Approved"           value={approved}            sub={`${approvalRate}% approval rate`} icon={TrendingUp}    color="bg-green-50 text-green-600" />
            <StatCard title="Students Funded"    value={paidCount}           sub="Funds disbursed"                  icon={Users}         color="bg-purple-50 text-purple-600" />
            <StatCard title="Total Disbursed"    value={formatKES(totalDisbursed)} sub="All paid amounts"           icon={Wallet}        color="bg-orange-50 text-orange-600" />
          </div>

          {/* Charts row */}
          <div className="grid md:grid-cols-2 gap-5">

            {/* Status distribution pie */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-blue-50 flex items-center justify-center">
                  <FileBarChart2 size={12} className="text-primary" />
                </div>
                Status Distribution
              </h3>
              {statusPie.length === 0 ? (
                <p className="text-center text-gray-300 py-10 text-sm">No data</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statusPie} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                      {statusPie.map((entry, i) => (
                        <Cell key={i} fill={STATUS_COLORS[entry.name] || "#9CA3AF"} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={(v) => <span className="text-xs text-gray-600">{v}</span>} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* School level bar chart */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-blue-50 flex items-center justify-center">
                  <GraduationCap size={12} className="text-primary" />
                </div>
                By School Level
              </h3>
              {levelData.length === 0 ? (
                <p className="text-center text-gray-300 py-10 text-sm">No data</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={levelData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total"    name="Total"    radius={[4, 4, 0, 0]}>
                      {levelData.map((e, i) => <Cell key={i} fill={LEVEL_COLORS[e.name] || "#9CA3AF"} />)}
                    </Bar>
                    <Bar dataKey="approved" name="Approved" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

          </div>

          {/* Ward breakdown bar chart */}
          {wardData.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-blue-50 flex items-center justify-center">
                  <MapPin size={12} className="text-primary" />
                </div>
                Applications by Ward
              </h3>
              <ResponsiveContainer width="100%" height={Math.max(200, wardData.length * 42)}>
                <BarChart data={wardData} layout="vertical" margin={{ top: 0, right: 20, left: 90, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={85} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend formatter={(v) => <span className="text-xs text-gray-600">{v}</span>} iconType="circle" iconSize={8} />
                  <Bar dataKey="total"    name="Total"    fill="#3B82F6" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="approved" name="Approved" fill="#10B981" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="rejected" name="Rejected" fill="#EF4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Ward detail table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <MapPin size={14} className="text-primary" /> Ward Detail Table
            </h3>
            {wardData.length === 0 ? (
              <p className="text-center text-gray-300 py-8 text-sm">No ward data available</p>
            ) : (
              <div className="overflow-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Ward", "Total", "Submitted", "Approved", "Rejected", "Approval Rate"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {wardData.map((w) => {
                      const rate = w.total > 0 ? Math.round((w.approved / w.total) * 100) : 0;
                      return (
                        <tr key={w.name} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3 font-medium text-gray-800">{w.name}</td>
                          <td className="px-4 py-3 text-gray-600">{w.total}</td>
                          <td className="px-4 py-3 text-yellow-600">{w.total - w.approved - w.rejected}</td>
                          <td className="px-4 py-3 text-green-600 font-medium">{w.approved}</td>
                          <td className="px-4 py-3 text-red-500">{w.rejected}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden min-w-[60px]">
                                <div className="h-full bg-green-500 rounded-full" style={{ width: `${rate}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0">{rate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-bold text-gray-700">TOTAL</td>
                      <td className="px-4 py-3 font-bold text-gray-700">{totalApps}</td>
                      <td className="px-4 py-3 font-bold text-yellow-600">{submitted}</td>
                      <td className="px-4 py-3 font-bold text-green-600">{approved}</td>
                      <td className="px-4 py-3 font-bold text-red-500">{rejected}</td>
                      <td className="px-4 py-3 font-bold text-gray-700">{approvalRate}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
};

export default ReportsPage;
